import React, { useContext } from 'react';
import { CalendarDays, ChevronDown, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { CalendarDate, CalendarDateTime, parseDate, parseDateTime } from '@internationalized/date';
import {
  Button,
  Calendar,
  CalendarCell,
  CalendarGrid,
  CalendarGridBody,
  CalendarGridHeader,
  CalendarHeaderCell,
  CalendarHeading,
  CalendarMonthPicker,
  CalendarYearPicker,
  DateInput,
  DatePicker as AriaDatePicker,
  DatePickerStateContext,
  DateSegment,
  Dialog,
  Group,
  I18nProvider,
  ListBox,
  ListBoxItem,
  Popover,
  Select,
  SelectValue,
  TimeField,
  type DateValue,
  type Key,
} from 'react-aria-components';
import './DateTimePicker.css';

interface DateTimePickerProps {
  value: string;
  onChange: (value: string) => void;
  ariaLabel: string;
  clearLabel: string;
  ariaDescribedBy?: string;
  min?: string;
  max?: string;
  required?: boolean;
  dateSelectionTime?: 'startOfDay' | 'currentTime';
  dateOnly?: boolean;
  invalid?: boolean;
}

interface CalendarSelectItem {
  id: number;
  formatted: string;
}

interface CalendarSelectProps {
  ariaLabel: string;
  className: string;
  items: CalendarSelectItem[];
  value: Key;
  onChange: (key: Key | null) => void;
}

const parseValue = (value?: string, dateOnly = false): CalendarDate | CalendarDateTime | null => {
  if (!value) return null;

  try {
    return dateOnly ? parseDate(value.slice(0, 10)) : parseDateTime(value);
  } catch {
    return null;
  }
};

const currentDate = new Date();
const defaultPlaceholderValue = new CalendarDateTime(
  currentDate.getFullYear(),
  currentDate.getMonth() + 1,
  currentDate.getDate(),
  currentDate.getHours(),
  currentDate.getMinutes(),
);
const defaultDatePlaceholderValue = new CalendarDate(
  currentDate.getFullYear(),
  currentDate.getMonth() + 1,
  currentDate.getDate(),
);

const CalendarSelect: React.FC<CalendarSelectProps> = ({
  ariaLabel,
  className,
  items,
  value,
  onChange,
}) => (
  <Select
    aria-label={ariaLabel}
    className={`app-date-time-calendar-select ${className}`}
    selectedKey={value}
    onSelectionChange={onChange}
  >
    <Button className="app-date-time-calendar-select-trigger">
      <SelectValue />
      <ChevronDown size={12} aria-hidden />
    </Button>
    <Popover
      className={`app-date-time-select-popover ${className}`}
      placement="bottom start"
    >
      <ListBox className="app-date-time-select-list" items={items}>
        {(item) => (
          <ListBoxItem
            id={item.id}
            className="app-date-time-select-option"
            textValue={item.formatted}
          >
            {item.formatted}
          </ListBoxItem>
        )}
      </ListBox>
    </Popover>
  </Select>
);

const CalendarTimeField: React.FC = () => {
  const state = useContext(DatePickerStateContext);

  if (!state) return null;

  return (
    <TimeField
      aria-label="Ora"
      className="app-date-time-calendar-time"
      value={state.timeValue}
      onChange={(time) => time && state.setTimeValue(time)}
      granularity="minute"
      hourCycle={24}
      shouldForceLeadingZeros
    >
      <DateInput className="app-date-time-calendar-time-input">
        {(segment) => <DateSegment segment={segment} />}
      </DateInput>
    </TimeField>
  );
};

const DateTimeInputGroup: React.FC = () => {
  const state = useContext(DatePickerStateContext);

  return (
    <Group className="app-date-time-input-group">
      <DateInput
        className="app-date-time-input"
        onClick={() => state?.setOpen(true)}
      >
        {(segment) => <DateSegment segment={segment} />}
      </DateInput>
      <Button
        className="app-date-time-calendar-trigger"
        aria-label="Deschide calendarul"
      >
        <CalendarDays size={17} aria-hidden />
      </Button>
    </Group>
  );
};

interface DateTimeCalendarProps {
  children: React.ReactNode;
  dateSelectionTime?: DateTimePickerProps['dateSelectionTime'];
}

const DateTimeCalendar: React.FC<DateTimeCalendarProps> = ({
  children,
  dateSelectionTime,
}) => {
  const state = useContext(DatePickerStateContext);

  const selectDate = dateSelectionTime && state
    ? (date: DateValue) => {
      const currentTime = new Date();
      const hour = dateSelectionTime === 'startOfDay' ? 0 : currentTime.getHours();
      const minute = dateSelectionTime === 'startOfDay' ? 0 : currentTime.getMinutes();
      state.setValue(new CalendarDateTime(date.year, date.month, date.day, hour, minute));
    }
    : undefined;

  return (
    <Calendar
      className="app-date-time-calendar"
      firstDayOfWeek="mon"
      onChange={selectDate}
    >
      {children}
    </Calendar>
  );
};

const DateTimeCalendarCell: React.FC<{ date: CalendarDate }> = ({ date }) => {
  const state = useContext(DatePickerStateContext);
  const closeAfterMobileSelection = () => {
    if (window.innerWidth <= 768) {
      // Let React Aria commit the selected date before dismissing the mobile popover.
      window.setTimeout(() => state?.setOpen(false), 0);
    }
  };

  return (
    <CalendarCell
      date={date}
      onClick={closeAfterMobileSelection}
      onDoubleClick={() => state?.setOpen(false)}
    />
  );
};

const DateTimePicker: React.FC<DateTimePickerProps> = ({
  value,
  onChange,
  ariaLabel,
  clearLabel,
  ariaDescribedBy,
  min,
  max,
  required = false,
  dateSelectionTime,
  dateOnly = false,
  invalid = false,
}) => (
  <I18nProvider locale="ro-RO">
    <div className={`app-date-time-picker ${invalid ? 'app-date-time-picker-invalid' : ''}`}>
      <AriaDatePicker
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        className="app-date-time-picker-control"
        value={parseValue(value, dateOnly)}
        onChange={(date) => onChange(date ? date.toString().slice(0, dateOnly ? 10 : 16) : '')}
        minValue={parseValue(min, dateOnly)}
        maxValue={parseValue(max, dateOnly)}
        placeholderValue={dateOnly ? defaultDatePlaceholderValue : defaultPlaceholderValue}
        granularity={dateOnly ? 'day' : 'minute'}
        hourCycle={24}
        shouldForceLeadingZeros
        firstDayOfWeek="mon"
        isRequired={required}
        isInvalid={invalid}
        validationBehavior="native"
        shouldCloseOnSelect={false}
      >
        <DateTimeInputGroup />

        <Popover
          className="app-date-time-popover"
          placement="bottom start"
          offset={4}
        >
          <Dialog className="app-date-time-dialog">
            <DateTimeCalendar dateSelectionTime={dateOnly ? undefined : dateSelectionTime}>
              <header className="app-date-time-calendar-header">
                <Button
                  slot="previous"
                  className="app-date-time-calendar-navigation"
                  aria-label="Luna precedentă"
                >
                  <ChevronLeft size={18} aria-hidden />
                </Button>

                <div className="app-date-time-calendar-header-fields">
                  <CalendarMonthPicker format="long">
                    {(props) => (
                      <CalendarSelect
                        ariaLabel={props['aria-label']}
                        className="month"
                        items={props.items}
                        value={props.value}
                        onChange={props.onChange}
                      />
                    )}
                  </CalendarMonthPicker>
                  <CalendarYearPicker visibleYears={100}>
                    {(props) => (
                      <CalendarSelect
                        ariaLabel={props['aria-label']}
                        className="year"
                        items={props.items}
                        value={props.value}
                        onChange={props.onChange}
                      />
                    )}
                  </CalendarYearPicker>
                  {!dateOnly && <CalendarTimeField />}
                </div>

                <Button
                  slot="next"
                  className="app-date-time-calendar-navigation"
                  aria-label="Luna următoare"
                >
                  <ChevronRight size={18} aria-hidden />
                </Button>
              </header>

              <CalendarHeading className="app-date-time-calendar-heading" />
              <CalendarGrid className="app-date-time-calendar-grid" weekdayStyle="short">
                <CalendarGridHeader>
                  {(day) => <CalendarHeaderCell>{day}</CalendarHeaderCell>}
                </CalendarGridHeader>
                <CalendarGridBody>
                  {(date) => <DateTimeCalendarCell date={date} />}
                </CalendarGridBody>
              </CalendarGrid>
            </DateTimeCalendar>
          </Dialog>
        </Popover>
      </AriaDatePicker>

      <button
        type="button"
        className="app-date-time-picker-clear"
        aria-label={clearLabel}
        title={clearLabel}
        disabled={!value}
        onClick={() => onChange('')}
      >
        <X size={15} aria-hidden />
      </button>
    </div>
  </I18nProvider>
);

export default DateTimePicker;
